import React, { useState } from "react";
import { Users, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { PT_DIOCESES } from "@/lib/dioceses";

const COUNTRIES = ["Portugal", "Espanha", "França", "Itália", "Alemanha", "Bélgica", "Suíça", "Luxemburgo", "Reino Unido", "Polónia", "Ucrânia", "Outro"];
const PERIODICITY = ["Semanal", "Quinzenal", "Mensal", "Outro"];
const WEEKDAYS = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];
const EMPTY = { group_name: "", name: "", email: "", phone: "", country: "Portugal", diocese: "", parish: "", city: "", postal_code: "", periodicity: "", prayer_day: "", prayer_time: "", members_count: "", message: "", consent_rgpd: false, website: "" };

const Field = ({ label, children }) => <div><Label>{label}</Label><div className="mt-1">{children}</div></div>;

function Pick({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder || "—"} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function GroupApply() {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const inp = (k, props = {}) => <Input value={form[k]} onChange={set(k)} data-testid={`apply-${k.replace(/_/g, "-")}`} {...props} />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_rgpd) { toast.error(t("form_consent")); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, members_count: form.members_count ? Number(form.members_count) : null };
      await api.post("/group-applications", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div>
      <section className="bg-th-blue th-pattern text-white py-14">
        <div className="max-w-3xl mx-auto px-4">
          <Users className="w-10 h-10 text-th-gold mb-3" />
          <h1 className="font-display font-bold text-4xl sm:text-5xl" data-testid="apply-title">{t("apply_title")}</h1>
          <p className="mt-3 text-white/75 text-lg">{t("apply_subtitle")}</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {done ? (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-2xl p-8 text-center" data-testid="apply-success">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-600" />
            <p className="text-lg">{t("apply_success")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-8" data-testid="apply-form">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <fieldset className="grid sm:grid-cols-2 gap-4">
              <legend className="font-display font-bold text-xl text-th-blue mb-3">{t("apply_section_group")}</legend>
              <div className="sm:col-span-2"><Field label={`${t("apply_group_name")} *`}>{inp("group_name", { required: true })}</Field></div>
              <Field label={`${t("form_country")} *`}><Pick value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v, diocese: "" }))} options={COUNTRIES} testid="apply-country" /></Field>
              <Field label={t("diocese")}>{form.country === "Portugal" ? <Pick value={form.diocese} onChange={set("diocese")} options={PT_DIOCESES} testid="apply-diocese" /> : inp("diocese")}</Field>
              <Field label={t("parish")}>{inp("parish")}</Field>
              <Field label={`${t("form_city")} *`}>{inp("city", { required: true })}</Field>
              <Field label={t("filter_postal")}>{inp("postal_code")}</Field>
              <Field label={t("apply_members")}>{inp("members_count", { type: "number", min: 1 })}</Field>
              <Field label={t("apply_periodicity")}><Pick value={form.periodicity} onChange={set("periodicity")} options={PERIODICITY} testid="apply-periodicity" /></Field>
              <Field label={t("apply_day")}><Pick value={form.prayer_day} onChange={set("prayer_day")} options={WEEKDAYS} testid="apply-prayer-day" /></Field>
              <Field label={t("apply_time")}>{inp("prayer_time", { type: "time" })}</Field>
            </fieldset>
            <fieldset className="grid sm:grid-cols-2 gap-4">
              <legend className="font-display font-bold text-xl text-th-blue mb-3">{t("apply_section_contact")}</legend>
              <div className="sm:col-span-2"><Field label={`${t("apply_responsible")} *`}>{inp("name", { required: true })}</Field></div>
              <Field label={`${t("form_email")} *`}>{inp("email", { required: true, type: "email" })}</Field>
              <Field label={`${t("form_phone")} *`}>{inp("phone", { required: true, type: "tel", minLength: 6 })}</Field>
              <div className="sm:col-span-2"><Field label={t("form_message")}><Textarea rows={4} value={form.message} onChange={set("message")} data-testid="apply-message" /></Field></div>
            </fieldset>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.consent_rgpd} data-testid="apply-consent" onCheckedChange={(v) => set("consent_rgpd")(!!v)} className="mt-0.5" />
              <span className="text-sm text-slate-600">{t("form_consent")}</span>
            </label>
            <Button type="submit" disabled={submitting} data-testid="apply-submit" className="w-full bg-th-blue text-white hover:bg-th-bluelight h-12 text-base">{submitting ? t("submitting") : t("apply_submit")}</Button>
          </form>
        )}
      </section>
    </div>
  );
}
